// src/pages/canvass/CanvassPage.tsx
// Mobile-first field canvassing interface with offline support
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  MapPin, Navigation2, List, Plus, AlertTriangle,
  X, ChevronDown, Loader2, User, Phone, Mail, Save,
  UserPlus, CheckCircle2, Sparkles, WifiOff
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useOffline } from '../../contexts/OfflineContext';
import { syncQueueService } from '../../services/offline/syncQueue.service';
import OfflineIndicator from '../../components/offline/OfflineIndicator';
import { workloadService } from '../../services/workload.service';
import { addressService } from '../../services/address.service';
import { surveyService } from '../../services/survey.service';
import { surveyResponseService } from '../../services/surveyResponse.service';
import { locationService } from '../../services/location.service';
import { projectService } from '../../services/project.service';
import { useLeadFormStore } from '../../store/leadFormStore';
import { Workload } from '../../types/workload.types';
import { Project } from '../../types/project.types';
import { WorkloadAddress, AddressStatus } from '../../types/address.types';
import { Survey } from '../../types/survey.types';
import GoogleMapProvider from '../../components/map/GoogleMapProvider';
import BaseMap from '../../components/map/BaseMap';
import { Marker, InfoWindow, Circle } from '@react-google-maps/api';
import SurveyPreview from '../../components/surveys/preview/SurveyPreview';
import { DispositionModal, AddContactModal, AddUnitModal, NewContactData, NewUnitData } from '../../components/canvass';

// Status colors for markers
const STATUS_COLORS: Record<AddressStatus, string> = {
  pending: '#9CA3AF', // gray
  visited: '#3B82F6', // blue
  not_home: '#F59E0B', // yellow
  refused: '#EF4444', // red
  completed: '#22C55E', // green
  discontinued: '#F97316', // orange
  inaccessible: '#8B5CF6', // purple
};

const CanvassPage: React.FC = () => {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const { isOnline, isSyncing, pendingCount, sync } = useOffline();

  // State
  const [workloads, setWorkloads] = useState<Workload[]>([]);
  const [selectedWorkload, setSelectedWorkload] = useState<Workload | null>(null);
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [addresses, setAddresses] = useState<WorkloadAddress[]>([]);
  const [selectedAddress, setSelectedAddress] = useState<WorkloadAddress | null>(null);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number; accuracy?: number; heading?: number } | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showDispositionModal, setShowDispositionModal] = useState(false);
  const [showSurveyModal, setShowSurveyModal] = useState(false);
  const [showEncounterModal, setShowEncounterModal] = useState(false);
  const [survey, setSurvey] = useState<Survey | null>(null);
  const [viewMode, setViewMode] = useState<'map' | 'list'>('map');
  const [filter, setFilter] = useState<AddressStatus | 'all'>('all');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCompleteWorkloadModal, setShowCompleteWorkloadModal] = useState(false);
  const [isCompletingWorkload, setIsCompletingWorkload] = useState(false);

  // Add Contact Modal state
  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const [newContact, setNewContact] = useState<NewContactData>({
    name: '',
    phone: '',
    email: '',
    notes: '',
    street: '',
    unit: '',
    city: '',
    state: '',
    zipCode: '',
    lat: 0,
    lng: 0,
    useMapPicker: false,
  });
  const [isAddingContact, setIsAddingContact] = useState(false);
  const [mapPickerActive, setMapPickerActive] = useState(false);

  // Target Info editing state
  const [showTargetInfoModal, setShowTargetInfoModal] = useState(false);
  const [targetInfo, setTargetInfo] = useState({
    name: '',
    phone: '',
    email: '',
    notes: '',
  });
  const [isSavingTargetInfo, setIsSavingTargetInfo] = useState(false);

  // Quick resident name capture in disposition modal
  const [quickResidentName, setQuickResidentName] = useState('');

  // Quick Add Unit state
  const [showAddUnitModal, setShowAddUnitModal] = useState(false);
  const [newUnit, setNewUnit] = useState<NewUnitData>({ unit: '', name: '' });
  const [isAddingUnit, setIsAddingUnit] = useState(false);

  // Lead capture state (for lead generation mode)
  const [showLeadCaptureModal, setShowLeadCaptureModal] = useState(false);
  const [pendingLeadAddress, setPendingLeadAddress] = useState<WorkloadAddress | null>(null);

  // Load user's assigned workloads
  useEffect(() => {
    const loadWorkloads = async () => {
      if (!currentUser) return;

      try {
        setIsLoading(true);
        const userWorkloads = await workloadService.getUserWorkloads(currentUser.uid);
        // Filter to only active workloads
        const activeWorkloads = userWorkloads.filter(w => w.status === 'active');
        setWorkloads(activeWorkloads);

        // Auto-select if only one workload
        if (activeWorkloads.length === 1) {
          handleSelectWorkload(activeWorkloads[0]);
        }
      } catch (error) {
        console.error('Error loading workloads:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadWorkloads();
  }, [currentUser]);

  // Watch user location with high accuracy for real-time tracking
  useEffect(() => {
    let watchId: number | null = null;

    if (navigator.geolocation) {
      watchId = navigator.geolocation.watchPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy,
            heading: position.coords.heading ?? undefined,
          });
          setLocationError(null);
        },
        (error) => {
          console.error('Location error:', error);
          setLocationError(error.message);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0, // Always get fresh position
        }
      );
    }

    return () => {
      if (watchId !== null) {
        navigator.geolocation.clearWatch(watchId);
      }
    };
  }, []);

  // Select workload and load addresses
  const handleSelectWorkload = async (workload: Workload) => {
    setSelectedWorkload(workload);
    setSelectedAddress(null);

    try {
      // Load addresses for this workload
      const workloadAddresses = await addressService.getWorkloadAddresses(workload.id);
      setAddresses(workloadAddresses);

      // Load survey if linked
      if (workload.surveyId) {
        const linkedSurvey = await surveyService.get(workload.surveyId);
        setSurvey(linkedSurvey);
      } else {
        setSurvey(null);
      }

      // Load project to check type
      if (workload.projectId) {
        const project = await projectService.getProject(workload.projectId);
        setCurrentProject(project);
      } else {
        setCurrentProject(null);
      }
    } catch (error) {
      console.error('Error loading workload data:', error);
    }
  };

  // Check if current workload is for a lead project
  const isLeadProject = currentProject?.projectType === 'lead_program';

  // Handle capturing a lead from the canvass view
  const handleCaptureLead = () => {
    if (currentProject) {
      // Navigate to lead creation with project pre-selected
      navigate(`/leads/new?projectId=${currentProject.id}`);
    }
  };

  // Handle marking workload as complete
  const handleCompleteWorkload = async () => {
    if (!selectedWorkload || !currentUser) return;

    setIsCompletingWorkload(true);
    try {
      await workloadService.updateStatus(selectedWorkload.id, 'completed', currentUser.uid);

      // Remove from active workloads list
      setWorkloads(prev => prev.filter(w => w.id !== selectedWorkload.id));
      setSelectedWorkload(null);
      setAddresses([]);
      setShowCompleteWorkloadModal(false);

      toast.success('Workload marked as complete!');
    } catch (error) {
      console.error('Error completing workload:', error);
      toast.error('Failed to complete workload. Please try again.');
    } finally {
      setIsCompletingWorkload(false);
    }
  };

  // Handle marker click
  const handleMarkerClick = (address: WorkloadAddress) => {
    setSelectedAddress(address);
    setQuickResidentName(address.residentName || '');
  };

  // Handle disposition selection
  const handleDisposition = async (status: AddressStatus) => {
    if (!selectedAddress || !selectedWorkload || !currentUser) return;

    // Save resident name if changed (run in background)
    const nameToSave = quickResidentName.trim();
    const nameChanged = nameToSave !== (selectedAddress.residentName || '');
    if (nameChanged && nameToSave) {
      addressService.updateResidentInfo(
        selectedWorkload.id,
        selectedAddress.id,
        { residentName: nameToSave }
      ).catch(error => console.error('Error saving resident name:', error));
    }

    // For "completed" with survey, open survey IMMEDIATELY
    // NOTE: Don't call addressService.updateStatus() here - surveyResponseService.create() handles it
    // This prevents duplicate status updates and race conditions
    if (status === 'completed' && survey) {
      setShowDispositionModal(false);
      setShowSurveyModal(true);

      // Update local state immediately for responsive UI (optimistic)
      setAddresses(prev => prev.map(a =>
        a.id === selectedAddress.id
          ? {
              ...a,
              status,
              lastVisitedAt: new Date(),
              lastVisitedBy: currentUser.uid,
              residentName: nameToSave || a.residentName,
            }
          : a
      ));

      // Update selected address with resident name for survey context
      setSelectedAddress(prev => prev ? { ...prev, residentName: nameToSave || prev.residentName } : null);

      // Status update will happen in surveyResponseService.create() after survey completion
      return;
    }

    // For "completed" on lead project without survey, open lead capture form
    if (status === 'completed' && isLeadProject && !survey) {
      setShowDispositionModal(false);

      // Store address data for lead capture
      setPendingLeadAddress({
        ...selectedAddress,
        residentName: nameToSave || selectedAddress.residentName,
      });
      setShowLeadCaptureModal(true);

      // Update local state immediately for responsive UI
      setAddresses(prev => prev.map(a =>
        a.id === selectedAddress.id
          ? {
              ...a,
              status,
              lastVisitedAt: new Date(),
              lastVisitedBy: currentUser.uid,
              residentName: nameToSave || a.residentName,
            }
          : a
      ));

      // Run status update in background (with offline support)
      const location = userLocation;
      if (!isOnline) {
        // Queue for later sync
        syncQueueService.enqueue(
          'addresses',
          selectedAddress.id,
          'update',
          {
            workloadId: selectedWorkload.id,
            addressId: selectedAddress.id,
            status,
            lastVisitedBy: currentUser.uid,
            lastVisitedByName: userProfile?.firstName + ' ' + userProfile?.lastName || 'Unknown',
            lastVisitedAt: new Date().toISOString(),
            location: location ? { lat: location.lat, lng: location.lng } : undefined,
            residentName: nameToSave || selectedAddress.residentName,
          }
        ).catch(error => console.error('Error queuing offline update:', error));
      } else {
        addressService.updateStatus(
          selectedWorkload.id,
          selectedAddress.id,
          status,
          currentUser.uid,
          userProfile?.firstName + ' ' + userProfile?.lastName || 'Unknown',
          location ? { lat: location.lat, lng: location.lng } : undefined
        ).catch(error => console.error('Background status update error:', error));
      }

      return;
    }

    // For other statuses, show loading and wait for update
    setIsSubmitting(true);

    try {
      // Use cached location - don't fetch fresh (faster)
      const location = userLocation;

      // If offline, queue the change for later sync
      if (!isOnline) {
        await syncQueueService.enqueue(
          'addresses',
          selectedAddress.id,
          'update',
          {
            workloadId: selectedWorkload.id,
            addressId: selectedAddress.id,
            status,
            lastVisitedBy: currentUser.uid,
            lastVisitedByName: userProfile?.firstName + ' ' + userProfile?.lastName || 'Unknown',
            lastVisitedAt: new Date().toISOString(),
            location: location ? { lat: location.lat, lng: location.lng } : undefined,
            residentName: nameToSave || selectedAddress.residentName,
          }
        );
        toast.success('Saved offline - will sync when connected', { icon: '📴' });
      } else {
        // Online: Update address status immediately
        await addressService.updateStatus(
          selectedWorkload.id,
          selectedAddress.id,
          status,
          currentUser.uid,
          userProfile?.firstName + ' ' + userProfile?.lastName || 'Unknown',
          location ? { lat: location.lat, lng: location.lng } : undefined
        );
      }

      // Update local state (optimistic for both online and offline)
      setAddresses(prev => prev.map(a =>
        a.id === selectedAddress.id
          ? {
              ...a,
              status,
              lastVisitedAt: new Date(),
              lastVisitedBy: currentUser.uid,
              residentName: nameToSave || a.residentName,
            }
          : a
      ));

      setShowDispositionModal(false);
      setSelectedAddress(null);
      setQuickResidentName('');
    } catch (error) {
      console.error('Error updating disposition:', error);
      toast.error('Failed to save disposition');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle survey completion
  const handleSurveyComplete = async (responses: any) => {
    if (!survey || !selectedAddress || !selectedWorkload || !currentUser) {
      setShowSurveyModal(false);
      setSelectedAddress(null);
      setQuickResidentName('');
      return;
    }

    // Show submitting state - keep modal context until done
    setIsSubmitting(true);
    const savedAddress = selectedAddress;
    const savedResidentName = savedAddress.residentName || quickResidentName;

    try {
      const location = userLocation;

      // If offline, queue the survey response for later sync
      if (!isOnline) {
        await syncQueueService.enqueue(
          'surveyResponses',
          `${savedAddress.id}-${Date.now()}`, // Generate unique ID
          'create',
          {
            surveyId: survey.id,
            surveyVersion: survey.version,
            workloadId: selectedWorkload.id,
            workloadName: selectedWorkload.name,
            addressId: savedAddress.id,
            projectId: selectedWorkload.projectId,
            responses: responses,
            startedAt: new Date().toISOString(),
            completedAt: new Date().toISOString(),
            residentName: savedResidentName || undefined,
            residentPhone: savedAddress.residentPhone || undefined,
            residentEmail: savedAddress.residentEmail || undefined,
            addressStreet: savedAddress.street,
            submittedBy: currentUser.uid,
            submittedByName: userProfile?.firstName + ' ' + userProfile?.lastName || 'Field User',
            location: location ? { lat: location.lat, lng: location.lng, accuracy: 10, timestamp: Date.now(), source: 'gps' } : undefined,
          }
        );
        toast.success('Survey saved offline - will sync when connected', { icon: '📴' });
      } else {
        // Online: Save survey response with resident info (indexed for searching)
        // This also updates address status and workload progress automatically
        await surveyResponseService.create(
          {
            surveyId: survey.id,
            surveyVersion: survey.version,
            workloadId: selectedWorkload.id,
            workloadName: selectedWorkload.name,
            addressId: savedAddress.id,
            projectId: selectedWorkload.projectId,
            responses: responses,
            startedAt: new Date(),
            residentName: savedResidentName || undefined,
            residentPhone: savedAddress.residentPhone || undefined,
            residentEmail: savedAddress.residentEmail || undefined,
            addressStreet: savedAddress.street,
          },
          currentUser.uid,
          userProfile?.firstName + ' ' + userProfile?.lastName || 'Field User',
          location ? { lat: location.lat, lng: location.lng, accuracy: 10, timestamp: Date.now(), source: 'gps' } : undefined
        );
      }

      // Update local state after successful save (optimistic for both online and offline)
      setAddresses(prev => prev.map(a =>
        a.id === savedAddress.id
          ? { ...a, status: 'completed' as const, lastVisitedAt: new Date(), lastVisitedBy: currentUser.uid }
          : a
      ));

      // Close modal only after successful submission
      setShowSurveyModal(false);
      setSelectedAddress(null);
      setQuickResidentName('');
    } catch (error) {
      console.error('Error saving survey response:', error);
      // Keep modal open on error so user can retry
      toast.error('Failed to save survey. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle lead-capture gate triggered from within the survey.
  // When a yes_no question with `triggerLeadCapture` is answered "yes", the
  // survey exits early and we navigate to the full lead form pre-filled with
  // the current address. The canvasser gets the complete lead flow (parent
  // info, children, schedule tour, documents, review).
  const handleLeadCaptureFromSurvey = useCallback(async (partialResponses: Record<string, any>) => {
    if (!selectedAddress || !selectedWorkload || !currentUser || !currentProject) {
      setShowSurveyModal(false);
      return;
    }

    const savedAddress = selectedAddress;
    const savedResidentName = savedAddress.residentName || quickResidentName;

    // Close survey modal.
    setShowSurveyModal(false);
    setSelectedAddress(null);
    setQuickResidentName('');

    // Save partial survey response so the survey isn't totally lost.
    if (survey && Object.keys(partialResponses).length > 0) {
      try {
        const location = userLocation;
        await surveyResponseService.create(
          {
            surveyId: survey.id,
            surveyVersion: survey.version,
            workloadId: selectedWorkload.id,
            workloadName: selectedWorkload.name,
            addressId: savedAddress.id,
            projectId: selectedWorkload.projectId,
            responses: partialResponses,
            startedAt: new Date(),
            residentName: savedResidentName || undefined,
            residentPhone: savedAddress.residentPhone || undefined,
            residentEmail: savedAddress.residentEmail || undefined,
            addressStreet: savedAddress.street,
          },
          currentUser.uid,
          userProfile?.name || 'Field User',
          location ? { lat: location.lat, lng: location.lng, accuracy: 10, timestamp: Date.now(), source: 'gps' } : undefined
        );
      } catch (err) {
        console.warn('Failed to save partial survey before lead form:', err);
        // Continue to lead form anyway — the lead is more important than the survey response.
      }
    }

    // Pre-fill the lead form store with address data + warm/valid status
    // (the person said "yes" to the gate question in person, so they're at
    // least warm and the lead is valid). Then navigate — LeadForm reads from
    // the store, not URL params.
    const leadFormStore = useLeadFormStore.getState();
    leadFormStore.resetForm();
    leadFormStore.setProjectId(currentProject.id);
    leadFormStore.setParentData({
      firstName: savedResidentName?.split(' ')[0] || '',
      lastName: savedResidentName?.split(' ').slice(1).join(' ') || '',
      phone: savedAddress.residentPhone || '',
      email: savedAddress.residentEmail || '',
    });
    leadFormStore.setAddressData({
      street: savedAddress.street,
      unit: savedAddress.unit || '',
      city: savedAddress.city,
      state: savedAddress.state,
      zipCode: savedAddress.zipCode,
    });
    leadFormStore.updateFormData({
      initialStatus: 'warm',
      initialDetermination: 'valid',
    });
    navigate('/leads/new/full');
  }, [selectedAddress, selectedWorkload, currentUser, currentProject, survey, quickResidentName, userLocation, userProfile, navigate]);

  // Handle survey discontinued (person walked away mid-survey)
  const handleSurveyDiscontinued = async (partialResponses: any) => {
    if (!selectedAddress || !selectedWorkload || !currentUser) {
      setShowSurveyModal(false);
      setSelectedAddress(null);
      setQuickResidentName('');
      return;
    }

    // Capture values before clearing state
    const savedAddress = selectedAddress;
    const savedResidentName = savedAddress.residentName || quickResidentName;
    const location = userLocation;

    // Close modal immediately for responsive UX (discontinued is lower priority)
    setShowSurveyModal(false);
    setSelectedAddress(null);
    setQuickResidentName('');

    // Update local state immediately (optimistic)
    setAddresses(prev => prev.map(a =>
      a.id === savedAddress.id
        ? { ...a, status: 'discontinued' as const, lastVisitedAt: new Date(), lastVisitedBy: currentUser.uid }
        : a
    ));

    // Save partial responses and update status via surveyResponseService
    // NOTE: surveyResponseService.create() handles addressService.updateStatus() internally
    // so we don't call it separately here to avoid duplicate updates
    try {
      // If offline, queue the changes
      if (!isOnline) {
        if (survey && Object.keys(partialResponses).length > 0) {
          await syncQueueService.enqueue(
            'surveyResponses',
            `${savedAddress.id}-${Date.now()}`,
            'create',
            {
              surveyId: survey.id,
              surveyVersion: survey.version,
              workloadId: selectedWorkload.id,
              workloadName: selectedWorkload.name,
              addressId: savedAddress.id,
              projectId: selectedWorkload.projectId,
              responses: partialResponses,
              startedAt: new Date().toISOString(),
              discontinued: true,
              residentName: savedResidentName || undefined,
              residentPhone: savedAddress.residentPhone || undefined,
              residentEmail: savedAddress.residentEmail || undefined,
              addressStreet: savedAddress.street,
              submittedBy: currentUser.uid,
              submittedByName: userProfile?.firstName + ' ' + userProfile?.lastName || 'Field User',
              location: location ? { lat: location.lat, lng: location.lng, accuracy: 10, timestamp: Date.now(), source: 'gps' } : undefined,
            }
          );
        } else {
          // No partial responses - just queue the address status update
          await syncQueueService.enqueue(
            'addresses',
            savedAddress.id,
            'update',
            {
              workloadId: selectedWorkload.id,
              addressId: savedAddress.id,
              status: 'discontinued',
              lastVisitedBy: currentUser.uid,
              lastVisitedByName: userProfile?.firstName + ' ' + userProfile?.lastName || 'Unknown',
              lastVisitedAt: new Date().toISOString(),
              location: location ? { lat: location.lat, lng: location.lng } : undefined,
            }
          );
        }
        // Silent offline save for discontinued (user has already moved on)
      } else {
        // Online: save normally
        if (survey && Object.keys(partialResponses).length > 0) {
          await surveyResponseService.create(
            {
              surveyId: survey.id,
              surveyVersion: survey.version,
              workloadId: selectedWorkload.id,
              workloadName: selectedWorkload.name,
              addressId: savedAddress.id,
              projectId: selectedWorkload.projectId,
              responses: partialResponses,
              startedAt: new Date(),
              discontinued: true,
              residentName: savedResidentName || undefined,
              residentPhone: savedAddress.residentPhone || undefined,
              residentEmail: savedAddress.residentEmail || undefined,
              addressStreet: savedAddress.street,
            },
            currentUser.uid,
            userProfile?.firstName + ' ' + userProfile?.lastName || 'Field User',
            location ? { lat: location.lat, lng: location.lng, accuracy: 10, timestamp: Date.now(), source: 'gps' } : undefined
          );
        } else {
          // No partial responses to save - just update the address status directly
          await addressService.updateStatus(
            selectedWorkload.id,
            savedAddress.id,
            'discontinued',
            currentUser.uid,
            userProfile?.firstName + ' ' + userProfile?.lastName || 'Unknown',
            location ? { lat: location.lat, lng: location.lng } : undefined
          );
        }
      }
    } catch (error) {
      console.error('Error handling discontinued survey:', error);
      // Don't show error to user for discontinued - they've already moved on
    }
  };

  // Handle adding a new contact
  const handleAddContact = async () => {
    if (!selectedWorkload || !currentUser) return;
    if (!newContact.street || !newContact.city || !newContact.state) {
      return; // Basic validation
    }

    setIsAddingContact(true);

    try {
      // Get canvasser's current location for accountability
      const canvasserLocation = userLocation || await locationService.getCurrentPosition()
        .then(pos => ({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }))
        .catch(() => null);

      // Use provided coordinates or fall back to canvasser location
      const addressLat = newContact.lat || canvasserLocation?.lat || 0;
      const addressLng = newContact.lng || canvasserLocation?.lng || 0;

      // Create the new address in the workload
      const newAddress = await addressService.create(selectedWorkload.id, {
        street: newContact.street,
        unit: newContact.unit || undefined,
        city: newContact.city,
        state: newContact.state,
        zipCode: newContact.zipCode,
        lat: addressLat,
        lng: addressLng,
        residentName: newContact.name || undefined,
        residentPhone: newContact.phone || undefined,
        residentEmail: newContact.email || undefined,
        notes: newContact.notes ? `Added by canvasser: ${newContact.notes}` : 'Added by canvasser in field',
      });

      // Add to local state
      setAddresses(prev => [...prev, newAddress]);

      // Reset form
      setNewContact({
        name: '',
        phone: '',
        email: '',
        notes: '',
        street: '',
        unit: '',
        city: '',
        state: '',
        zipCode: '',
        lat: 0,
        lng: 0,
        useMapPicker: false,
      });

      setShowAddContactModal(false);
      setMapPickerActive(false);

      // Select the new address and open disposition modal
      setSelectedAddress(newAddress);
      setShowDispositionModal(true);
    } catch (error) {
      console.error('Error adding contact:', error);
    } finally {
      setIsAddingContact(false);
    }
  };

  // Handle map click for picking location
  const handleMapClickForContact = useCallback((e: google.maps.MapMouseEvent) => {
    if (mapPickerActive && e.latLng) {
      setNewContact(prev => ({
        ...prev,
        lat: e.latLng!.lat(),
        lng: e.latLng!.lng(),
      }));
    }
  }, [mapPickerActive]);

  // Open target info modal for selected address
  const openTargetInfoModal = () => {
    if (!selectedAddress) return;
    setTargetInfo({
      name: selectedAddress.residentName || '',
      phone: selectedAddress.residentPhone || '',
      email: selectedAddress.residentEmail || '',
      notes: selectedAddress.notes || '',
    });
    setShowTargetInfoModal(true);
  };

  // Save target info
  const handleSaveTargetInfo = async () => {
    if (!selectedAddress || !selectedWorkload) return;

    setIsSavingTargetInfo(true);
    try {
      await addressService.updateResidentInfo(
        selectedWorkload.id,
        selectedAddress.id,
        {
          residentName: targetInfo.name || undefined,
          residentPhone: targetInfo.phone || undefined,
          residentEmail: targetInfo.email || undefined,
          notes: targetInfo.notes || undefined,
        }
      );

      // Update local state
      setAddresses(prev => prev.map(a =>
        a.id === selectedAddress.id
          ? {
              ...a,
              residentName: targetInfo.name || undefined,
              residentPhone: targetInfo.phone || undefined,
              residentEmail: targetInfo.email || undefined,
              notes: targetInfo.notes || undefined,
            }
          : a
      ));

      // Update selectedAddress
      setSelectedAddress(prev => prev ? {
        ...prev,
        residentName: targetInfo.name || undefined,
        residentPhone: targetInfo.phone || undefined,
        residentEmail: targetInfo.email || undefined,
        notes: targetInfo.notes || undefined,
      } : null);

      setShowTargetInfoModal(false);
    } catch (error) {
      console.error('Error saving target info:', error);
    } finally {
      setIsSavingTargetInfo(false);
    }
  };

  // Quick add unit at same address
  const handleAddUnit = async () => {
    if (!selectedAddress || !selectedWorkload || !currentUser || !newUnit.unit.trim()) return;

    setIsAddingUnit(true);
    try {
      // Get location from selected address or fall back to user location
      const lat = selectedAddress.location?.lat ?? userLocation?.lat ?? 0;
      const lng = selectedAddress.location?.lng ?? userLocation?.lng ?? 0;

      // Create new address with same location but different unit
      const newAddress = await addressService.create(selectedWorkload.id, {
        street: selectedAddress.street,
        unit: newUnit.unit.trim(),
        city: selectedAddress.city,
        state: selectedAddress.state,
        zipCode: selectedAddress.zipCode,
        lat,
        lng,
        residentName: newUnit.name.trim() || null,
        notes: `Unit added by canvasser at ${selectedAddress.street}`,
      });

      // Add to local state
      setAddresses(prev => [...prev, newAddress]);

      // Reset and close modal
      setNewUnit({ unit: '', name: '' });
      setShowAddUnitModal(false);

      // Select the new address and open disposition modal
      setSelectedAddress(newAddress);
      setShowDispositionModal(true);
    } catch (error) {
      console.error('Error adding unit:', error);
    } finally {
      setIsAddingUnit(false);
    }
  };

  // Filter addresses
  const filteredAddresses = filter === 'all'
    ? addresses
    : addresses.filter(a => a.status === filter);

  // Map center state - only updates when explicitly changed, not on every user location update
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number }>({ lat: 39.8283, lng: -98.5795 });
  const [hasInitializedMap, setHasInitializedMap] = useState(false);

  // Initialize map center once when we have location or addresses
  useEffect(() => {
    if (hasInitializedMap) return;

    if (addresses.length > 0) {
      // Center on addresses
      const center = {
        lat: addresses.reduce((sum, a) => sum + a.location.lat, 0) / addresses.length,
        lng: addresses.reduce((sum, a) => sum + a.location.lng, 0) / addresses.length,
      };
      setMapCenter(center);
      setHasInitializedMap(true);
    } else if (userLocation) {
      // Fall back to user location
      setMapCenter(userLocation);
      setHasInitializedMap(true);
    }
  }, [addresses, userLocation, hasInitializedMap]);

  // Reset map initialization when workload changes
  useEffect(() => {
    setHasInitializedMap(false);
  }, [selectedWorkload?.id]);

  // Center map on user location (only when button clicked)
  const centerOnMe = useCallback(() => {
    if (mapInstance && userLocation) {
      mapInstance.panTo(userLocation);
      mapInstance.setZoom(17); // Close zoom for navigation
    }
  }, [mapInstance, userLocation]);

  // Center map to fit all addresses in the list
  const centerOnList = useCallback(() => {
    if (mapInstance && filteredAddresses.length > 0 && typeof google !== 'undefined' && google.maps?.LatLngBounds) {
      const bounds = new google.maps.LatLngBounds();
      filteredAddresses.forEach(addr => {
        bounds.extend(addr.location);
      });
      mapInstance.fitBounds(bounds, { top: 50, right: 50, bottom: 100, left: 50 });
    }
  }, [mapInstance, filteredAddresses]);

  // Progress stats
  const stats = {
    total: addresses.length,
    completed: addresses.filter(a => a.status === 'completed').length,
    pending: addresses.filter(a => a.status === 'pending').length,
    notHome: addresses.filter(a => a.status === 'not_home').length,
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col overflow-x-hidden">
      {/* Offline Indicator - Shows when offline or has pending changes */}
      {(!isOnline || pendingCount > 0) && (
        <OfflineIndicator
          isOnline={isOnline}
          pendingChanges={pendingCount}
          isSyncing={isSyncing}
          onSync={sync}
          variant="bar"
        />
      )}

      {/* Header */}
      <header className="bg-indigo-600 text-white p-3 shadow-lg overflow-hidden">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center flex-shrink-0">
            <MapPin className="h-5 w-5 mr-1.5" />
            <h1 className="text-base font-bold">Canvass</h1>
            {/* Offline badge in header */}
            {!isOnline && (
              <span className="ml-2 px-1.5 py-0.5 bg-red-500 text-white text-xs rounded-full flex items-center">
                <WifiOff className="h-3 w-3 mr-0.5" />
                Offline
              </span>
            )}
          </div>

          {/* Workload Selector */}
          <div className="relative flex-1 min-w-0 max-w-[65%]">
            <select
              value={selectedWorkload?.id || ''}
              onChange={(e) => {
                const wl = workloads.find(w => w.id === e.target.value);
                if (wl) handleSelectWorkload(wl);
              }}
              className="w-full bg-indigo-700 text-white border border-indigo-500 rounded-lg px-2 py-1.5 pr-7 text-xs appearance-none cursor-pointer truncate"
            >
              <option value="">Select Workload</option>
              {workloads.map(wl => (
                <option key={wl.id} value={wl.id}>{wl.name}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none" />
          </div>
        </div>

        {/* Progress Bar */}
        {selectedWorkload && (
          <div className="mt-2">
            <div className="flex justify-between items-center text-xs mb-1">
              <span>{stats.completed}/{stats.total} done</span>
              <span>{stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0}%</span>
            </div>
            <div className="h-1.5 bg-indigo-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-green-400 transition-all duration-300"
                style={{ width: `${stats.total > 0 ? (stats.completed / stats.total) * 100 : 0}%` }}
              />
            </div>
            {/* Mark Workload Complete Button */}
            <button
              onClick={() => setShowCompleteWorkloadModal(true)}
              className="mt-2 w-full flex items-center justify-center gap-1.5 px-2 py-1.5 bg-green-500 hover:bg-green-600 rounded-lg text-xs font-medium transition-colors"
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
              Mark Complete
            </button>
          </div>
        )}
      </header>

      {/* Location Warning */}
      {locationError && (
        <div className="bg-yellow-50 border-b border-yellow-200 p-2 flex items-center text-sm text-yellow-800">
          <AlertTriangle className="h-4 w-4 mr-2 flex-shrink-0" />
          <span>Location unavailable: {locationError}</span>
        </div>
      )}

      {/* No Workloads */}
      {workloads.length === 0 && (
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="text-center">
            <List className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <h2 className="text-lg font-medium text-gray-900 mb-2">No Active Workloads</h2>
            <p className="text-gray-500">
              You don't have any workloads assigned. Contact your supervisor for assignments.
            </p>
          </div>
        </div>
      )}

      {/* Main Content */}
      {selectedWorkload && (
        <>
          {/* View Toggle & Filter */}
          <div className="bg-white border-b px-2 py-1.5 flex items-center justify-between gap-2 overflow-hidden">
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={() => setViewMode('map')}
                className={`p-1.5 rounded ${viewMode === 'map' ? 'bg-indigo-100 text-indigo-600' : 'text-gray-500'}`}
              >
                <Navigation2 className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-indigo-100 text-indigo-600' : 'text-gray-500'}`}
              >
                <List className="h-4 w-4" />
              </button>
            </div>

            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as AddressStatus | 'all')}
              className="text-xs border rounded px-1.5 py-1 min-w-0 flex-1 max-w-[140px]"
            >
              <option value="all">All ({addresses.length})</option>
              <option value="pending">Pending ({stats.pending})</option>
              <option value="not_home">Not Home ({stats.notHome})</option>
              <option value="completed">Done ({stats.completed})</option>
            </select>

            {/* Quick Add Lead Button (for lead projects) */}
            {isLeadProject && (
              <button
                onClick={handleCaptureLead}
                className="p-1.5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-lg flex-shrink-0 shadow-sm"
                title="Add Lead"
              >
                <Sparkles className="h-4 w-4" />
              </button>
            )}

            {/* Add Contact Button */}
            <button
              onClick={() => setShowAddContactModal(true)}
              className="p-1.5 bg-indigo-600 text-white rounded-lg flex-shrink-0"
              title="Add Contact"
            >
              <UserPlus className="h-4 w-4" />
            </button>
          </div>

          {/* Map View */}
          {viewMode === 'map' && (
            <div className="flex-1 relative">
              {/* Map Picker Indicator */}
              {mapPickerActive && (
                <div className="absolute top-2 left-1/2 -translate-x-1/2 z-20 bg-indigo-600 text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg flex items-center">
                  <MapPin className="h-4 w-4 mr-2 animate-bounce" />
                  Tap to set location
                </div>
              )}

              {/* Floating Action Buttons - Left side to avoid Google zoom controls */}
              <div className="absolute left-4 bottom-20 z-20 flex flex-col gap-3">
                {/* Center on List Button */}
                <button
                  onClick={centerOnList}
                  disabled={filteredAddresses.length === 0}
                  className={`w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all ${
                    filteredAddresses.length > 0
                      ? 'bg-indigo-500 text-white hover:bg-indigo-600 active:scale-95'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                  title="Center on all addresses"
                >
                  <List className="h-5 w-5" />
                </button>

                {/* Center on Me Button */}
                <button
                  onClick={centerOnMe}
                  disabled={!userLocation}
                  className={`w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all ${
                    userLocation
                      ? 'bg-blue-500 text-white hover:bg-blue-600 active:scale-95'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                  title={userLocation ? 'Center on my location' : 'Getting location...'}
                >
                  <Navigation2 className="h-5 w-5" />
                </button>

                {/* Quick Add Lead Button (for lead projects) */}
                {isLeadProject && (
                  <button
                    onClick={handleCaptureLead}
                    className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 text-white rounded-full shadow-lg flex items-center justify-center hover:from-emerald-600 hover:to-teal-700 active:scale-95 transition-all"
                    title="Add Lead"
                  >
                    <Sparkles className="h-6 w-6" />
                  </button>
                )}

                {/* Add Contact Button */}
                <button
                  onClick={() => setShowAddContactModal(true)}
                  className="w-14 h-14 bg-indigo-600 text-white rounded-full shadow-lg flex items-center justify-center hover:bg-indigo-700 active:scale-95 transition-transform"
                  title="Add Contact"
                >
                  <UserPlus className="h-6 w-6" />
                </button>
              </div>

              {/* Map container with absolute positioning for proper height */}
              <div className="absolute inset-0 z-10" style={{ touchAction: 'none' }}>
              <GoogleMapProvider>
                <BaseMap
                  center={mapCenter}
                  zoom={16}
                  height="100%"
                  onMapLoad={(map) => setMapInstance(map)}
                  onClick={mapPickerActive ? (pos) => {
                    setNewContact(prev => ({
                      ...prev,
                      lat: pos.lat,
                      lng: pos.lng,
                    }));
                    setMapPickerActive(false);
                  } : undefined}
                >
                  {/* User Location - Accuracy Circle */}
                  {userLocation && userLocation.accuracy && (
                    <Circle
                      center={userLocation}
                      radius={userLocation.accuracy}
                      options={{
                        fillColor: '#3B82F6',
                        fillOpacity: 0.1,
                        strokeColor: '#3B82F6',
                        strokeOpacity: 0.3,
                        strokeWeight: 1,
                      }}
                    />
                  )}

                  {/* User Location - Outer Pulse Ring */}
                  {userLocation && (
                    <Circle
                      center={userLocation}
                      radius={25}
                      options={{
                        fillColor: '#3B82F6',
                        fillOpacity: 0.2,
                        strokeColor: '#3B82F6',
                        strokeOpacity: 0.5,
                        strokeWeight: 2,
                      }}
                    />
                  )}

                  {/* User Location - Main Marker (Blue Dot) */}
                  {typeof google !== 'undefined' && google.maps?.SymbolPath && userLocation && (
                    <Marker
                      position={userLocation}
                      icon={{
                        path: google.maps.SymbolPath.CIRCLE,
                        scale: 10,
                        fillColor: '#3B82F6',
                        fillOpacity: 1,
                        strokeColor: '#fff',
                        strokeWeight: 3,
                      }}
                      title="You are here"
                      zIndex={1000} // Always on top
                    />
                  )}

                  {/* User Location - Direction Arrow (if heading available) */}
                  {typeof google !== 'undefined' && google.maps?.SymbolPath && userLocation && userLocation.heading !== undefined && (
                    <Marker
                      position={userLocation}
                      icon={{
                        path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                        scale: 5,
                        fillColor: '#1D4ED8',
                        fillOpacity: 1,
                        strokeColor: '#fff',
                        strokeWeight: 1,
                        rotation: userLocation.heading,
                        anchor: new google.maps.Point(0, 3),
                      }}
                      zIndex={1001}
                    />
                  )}

                  {/* Address Markers */}
                  {typeof google !== 'undefined' && google.maps?.SymbolPath && filteredAddresses.map(addr => (
                    <Marker
                      key={addr.id}
                      position={addr.location}
                      onClick={() => handleMarkerClick(addr)}
                      icon={{
                        path: google.maps.SymbolPath.CIRCLE,
                        scale: 10,
                        fillColor: STATUS_COLORS[addr.status],
                        fillOpacity: 0.9,
                        strokeColor: selectedAddress?.id === addr.id ? '#000' : '#fff',
                        strokeWeight: selectedAddress?.id === addr.id ? 3 : 2,
                      }}
                      title={`${addr.street} - ${addr.status}`}
                    />
                  ))}

                  {/* Info Window for Selected Address */}
                  {selectedAddress && (
                    <InfoWindow
                      position={selectedAddress.location}
                      onCloseClick={() => setSelectedAddress(null)}
                    >
                      <div className="p-2 min-w-[200px]">
                        <h3 className="font-medium text-gray-900">
                          {selectedAddress.street}
                          {selectedAddress.unit && <span className="text-gray-500"> Unit {selectedAddress.unit}</span>}
                        </h3>
                        {selectedAddress.residentName && (
                          <p className="text-sm text-indigo-600 font-medium">{selectedAddress.residentName}</p>
                        )}
                        <p className="text-sm text-gray-500">
                          {selectedAddress.city}, {selectedAddress.state} {selectedAddress.zipCode}
                        </p>
                        <div className="mt-2 flex items-center justify-between">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            selectedAddress.status === 'pending' ? 'bg-gray-100 text-gray-700' :
                            selectedAddress.status === 'completed' ? 'bg-green-100 text-green-700' :
                            selectedAddress.status === 'not_home' ? 'bg-yellow-100 text-yellow-700' :
                            selectedAddress.status === 'refused' ? 'bg-red-100 text-red-700' :
                            'bg-purple-100 text-purple-700'
                          }`}>
                            {selectedAddress.status.replace('_', ' ')}
                          </span>
                          <div className="flex gap-1">
                            <button
                              onClick={() => setShowAddUnitModal(true)}
                              className="px-2 py-1 border border-indigo-600 text-indigo-600 text-sm rounded hover:bg-indigo-50"
                              title="Add another unit at this address"
                            >
                              +Unit
                            </button>
                            <button
                              onClick={() => setShowDispositionModal(true)}
                              className="px-3 py-1 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700"
                            >
                              Update
                            </button>
                          </div>
                        </div>
                      </div>
                    </InfoWindow>
                  )}

                  {/* New Contact Location Marker (when picking on map) */}
                  {typeof google !== 'undefined' && google.maps?.SymbolPath && newContact.lat !== 0 && showAddContactModal && (
                    <Marker
                      position={{ lat: newContact.lat, lng: newContact.lng }}
                      icon={{
                        path: google.maps.SymbolPath.CIRCLE,
                        scale: 12,
                        fillColor: '#10B981',
                        fillOpacity: 1,
                        strokeColor: '#fff',
                        strokeWeight: 3,
                      }}
                      title="New Contact Location"
                    />
                  )}
                </BaseMap>
              </GoogleMapProvider>
              </div>
            </div>
          )}

          {/* List View */}
          {viewMode === 'list' && (
            <div className="flex-1 overflow-y-auto">
              <ul className="divide-y divide-gray-200">
                {filteredAddresses.map(addr => (
                  <li
                    key={addr.id}
                    onClick={() => {
                      setSelectedAddress(addr);
                      setQuickResidentName(addr.residentName || '');
                      setShowDispositionModal(true);
                    }}
                    className="p-4 bg-white hover:bg-gray-50 cursor-pointer flex items-center"
                  >
                    <div
                      className="w-3 h-3 rounded-full mr-3 flex-shrink-0"
                      style={{ backgroundColor: STATUS_COLORS[addr.status] }}
                    />
                    <div className="flex-1 min-w-0">
                      {addr.residentName && (
                        <p className="text-sm font-medium text-indigo-600 truncate">
                          {addr.residentName}
                        </p>
                      )}
                      <p className={`${addr.residentName ? 'text-sm' : 'font-medium'} text-gray-900 truncate`}>
                        {addr.street}
                        {addr.unit && <span className="text-gray-500"> Unit {addr.unit}</span>}
                      </p>
                      <p className="text-sm text-gray-500 truncate">
                        {addr.city}, {addr.state}
                      </p>
                    </div>
                    <ChevronDown className="h-5 w-5 text-gray-400 -rotate-90" />
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      {/* Disposition Modal */}
      {showDispositionModal && selectedAddress && (
        <DispositionModal
          selectedAddress={selectedAddress}
          quickResidentName={quickResidentName}
          onResidentNameChange={setQuickResidentName}
          onDisposition={handleDisposition}
          onClose={() => setShowDispositionModal(false)}
          onAddUnit={() => {
            setShowDispositionModal(false);
            setShowAddUnitModal(true);
          }}
          onEditTargetInfo={openTargetInfoModal}
          isSubmitting={isSubmitting}
          survey={survey}
          isLeadProject={isLeadProject}
        />
      )}

      {/* Survey Modal */}
      {showSurveyModal && survey && selectedAddress && (
        <div className="fixed inset-0 bg-white z-50 overflow-y-auto">
          <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
            <h2 className="font-semibold text-gray-900">{survey.name}</h2>
            <button
              onClick={() => {
                setShowSurveyModal(false);
                setSelectedAddress(null);
              }}
              className="p-2"
            >
              <X className="h-5 w-5 text-gray-400" />
            </button>
          </div>
          <div className="p-4">
            <SurveyPreview
              survey={survey}
              mode="response"
              onSubmit={handleSurveyComplete}
              onDiscontinued={handleSurveyDiscontinued}
              onLeadCaptureTriggered={handleLeadCaptureFromSurvey}
              onClose={() => {
                setShowSurveyModal(false);
                setSelectedAddress(null);
              }}
            />
          </div>
        </div>
      )}

      {/* Add Contact Modal */}
      {showAddContactModal && (
        <AddContactModal
          newContact={newContact}
          onContactChange={(updates) => setNewContact(prev => ({ ...prev, ...updates }))}
          onSubmit={handleAddContact}
          onClose={() => {
            setShowAddContactModal(false);
            setMapPickerActive(false);
          }}
          isSubmitting={isAddingContact}
          mapPickerActive={mapPickerActive}
          onToggleMapPicker={() => setMapPickerActive(prev => !prev)}
          userLocation={userLocation}
        />
      )}

      {/* Target Info Modal */}
      {showTargetInfoModal && selectedAddress && (
        <div className="fixed inset-0 bg-black/50 flex items-end justify-center z-50 md:items-center">
          <div className="bg-white w-full max-w-md rounded-t-2xl md:rounded-2xl shadow-xl">
            <div className="p-4 border-b flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-gray-900">Edit Target Info</h2>
                <p className="text-sm text-gray-500">
                  {selectedAddress.street}
                </p>
              </div>
              <button onClick={() => setShowTargetInfoModal(false)} className="p-2">
                <X className="h-5 w-5 text-gray-400" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Resident Name"
                  value={targetInfo.name}
                  onChange={(e) => setTargetInfo(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="tel"
                  placeholder="Phone Number"
                  value={targetInfo.phone}
                  onChange={(e) => setTargetInfo(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="email"
                  placeholder="Email Address"
                  value={targetInfo.email}
                  onChange={(e) => setTargetInfo(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              <textarea
                placeholder="Notes about this contact..."
                value={targetInfo.notes}
                onChange={(e) => setTargetInfo(prev => ({ ...prev, notes: e.target.value }))}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
              />
            </div>

            <div className="p-4 border-t flex gap-3">
              <button
                onClick={() => setShowTargetInfoModal(false)}
                className="flex-1 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTargetInfo}
                disabled={isSavingTargetInfo}
                className="flex-1 py-2.5 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center"
              >
                {isSavingTargetInfo ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Add Unit Modal */}
      {showAddUnitModal && selectedAddress && (
        <AddUnitModal
          selectedAddress={selectedAddress}
          newUnit={newUnit}
          onUnitChange={(updates) => setNewUnit(prev => ({ ...prev, ...updates }))}
          onSubmit={handleAddUnit}
          onClose={() => setShowAddUnitModal(false)}
          isSubmitting={isAddingUnit}
        />
      )}

      {/* Complete Workload Confirmation Modal */}
      {showCompleteWorkloadModal && selectedWorkload && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-sm rounded-2xl shadow-xl overflow-hidden">
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Mark Workload Complete?</h2>
              <p className="text-gray-600 mb-1">
                <strong>{selectedWorkload.name}</strong>
              </p>
              <p className="text-sm text-gray-500 mb-4">
                {stats.completed} of {stats.total} addresses completed ({Math.round((stats.completed / stats.total) * 100)}%)
              </p>
              <p className="text-sm text-gray-500 mb-6">
                This will mark the workload as complete and remove it from your active list. You won't be able to undo this action.
              </p>
            </div>
            <div className="flex border-t">
              <button
                onClick={() => setShowCompleteWorkloadModal(false)}
                disabled={isCompletingWorkload}
                className="flex-1 py-4 text-gray-700 font-medium hover:bg-gray-50 border-r disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCompleteWorkload}
                disabled={isCompletingWorkload}
                className="flex-1 py-4 text-green-600 font-medium hover:bg-green-50 disabled:opacity-50 flex items-center justify-center"
              >
                {isCompletingWorkload ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Completing...
                  </>
                ) : (
                  'Yes, Complete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lead Capture Modal (for lead generation mode) */}
      {showLeadCaptureModal && pendingLeadAddress && currentProject && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-sm rounded-2xl shadow-xl overflow-hidden">
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles className="h-8 w-8 text-emerald-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Capture Lead?</h2>
              <p className="text-gray-600 mb-1">
                <strong>{pendingLeadAddress.street}</strong>
                {pendingLeadAddress.unit && <span> Unit {pendingLeadAddress.unit}</span>}
              </p>
              {pendingLeadAddress.residentName && (
                <p className="text-emerald-600 font-medium mb-1">
                  {pendingLeadAddress.residentName}
                </p>
              )}
              <p className="text-sm text-gray-500 mb-4">
                {pendingLeadAddress.city}, {pendingLeadAddress.state} {pendingLeadAddress.zipCode}
              </p>
              <p className="text-sm text-gray-500">
                Would you like to capture lead details for this address?
              </p>
            </div>
            <div className="flex border-t">
              <button
                onClick={() => {
                  setShowLeadCaptureModal(false);
                  setPendingLeadAddress(null);
                  setSelectedAddress(null);
                  setQuickResidentName('');
                }}
                className="flex-1 py-4 text-gray-700 font-medium hover:bg-gray-50 border-r"
              >
                Skip
              </button>
              <button
                onClick={() => {
                  // Navigate to lead form with pre-filled address data
                  const params = new URLSearchParams({
                    projectId: currentProject.id,
                    street: pendingLeadAddress.street,
                    unit: pendingLeadAddress.unit || '',
                    city: pendingLeadAddress.city,
                    state: pendingLeadAddress.state,
                    zipCode: pendingLeadAddress.zipCode,
                    parentName: pendingLeadAddress.residentName || '',
                    phone: pendingLeadAddress.residentPhone || '',
                    email: pendingLeadAddress.residentEmail || '',
                    lat: pendingLeadAddress.location?.lat?.toString() || '',
                    lng: pendingLeadAddress.location?.lng?.toString() || '',
                    // Captured in person → warm. Determination starts 'pending';
                    // validity is set ONLY by the client recap import, never at
                    // field capture (was 'valid' → fabricated valid leads).
                    status: 'warm',
                    determination: 'pending',
                  });
                  setShowLeadCaptureModal(false);
                  setPendingLeadAddress(null);
                  setSelectedAddress(null);
                  setQuickResidentName('');
                  navigate(`/leads/new?${params.toString()}`);
                }}
                className="flex-1 py-4 text-emerald-600 font-medium hover:bg-emerald-50 flex items-center justify-center"
              >
                <Sparkles className="h-5 w-5 mr-2" />
                Capture Lead
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Legend - Bottom Bar */}
      {selectedWorkload && viewMode === 'map' && (
        <div className="bg-white border-t p-2">
          <div className="flex justify-around text-xs">
            {Object.entries(STATUS_COLORS).slice(0, 5).map(([status, color]) => (
              <div key={status} className="flex items-center">
                <div
                  className="w-3 h-3 rounded-full mr-1"
                  style={{ backgroundColor: color }}
                />
                <span className="text-gray-600 capitalize">{status.replace('_', ' ')}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CanvassPage;
